import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import {
  Box,
  Button,
  Divider,
  Card,
  CardHeader,
  CardContent,
  Container,
} from '@mui/material';
import { DateTime } from 'luxon';
import { DateTimePicker } from '@mui/x-date-pickers';
import AggregateTypeSelector from '../components/AggregateTypeSelector';
import DataTypeToggleGroup from '../components/DataTypeToggleGroup';
import DataViewerChart from '../components/DataViewerChart';
import UnitAutocomplete from '../components/UnitAutocomplete';
import DateRangeSelector from '../components/DateRangeSelector';
import useDataViewerData from '../hooks/useDataViewerData';
import useSelectedUnits from '@/features/data-viewer/hooks/useSelectedUnits';
import useLiveMode from '@/features/data-viewer/hooks/useLiveMode';
import useDateRange from '@/features/data-viewer/hooks/useDateRange';
import { useTranslation } from 'react-i18next';
const LIVEMODE_POLL_INTERVAL_MS = 5000; // 5 seconds

// Aggregation logic
const AGGREGATE_OPTIONS = ['Raw', 'Minutes5', 'Hour'];
const AGGREGATE_THRESHOLDS = {
  Raw: 0.25, // max days for Raw
  Minutes5: 7, // max days for 5 min
  Hour: Infinity, // max days for Hour (3 months)
};

function getAutoAggregateType(from, to) {
  const diffDays = (new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24);
  if (diffDays <= AGGREGATE_THRESHOLDS.Raw) return 'Raw';
  if (diffDays <= AGGREGATE_THRESHOLDS.Minutes5) return 'Minutes5';
  if (diffDays <= AGGREGATE_THRESHOLDS.Hour) return 'Hour';
  return 'Hour';
}

function getAggregateType({ from, to, requested }) {
  const diffDays = (new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24);
  if (!requested) return getAutoAggregateType(from, to);
  if (requested === 'Raw' && diffDays > AGGREGATE_THRESHOLDS.Raw)
    return getAutoAggregateType(from, to);
  if (requested === 'Minutes5' && diffDays > AGGREGATE_THRESHOLDS.Minutes5)
    return getAutoAggregateType(from, to);
  if (requested === 'Hour' && diffDays > AGGREGATE_THRESHOLDS.Hour)
    return getAutoAggregateType(from, to);
  return requested;
}

const DataViewerPage = () => {
  const { t } = useTranslation('view-data');
  // Live mode polling interval ref
  const [aggregateOverride, setAggregateOverride] = React.useState(null); // null means auto
  // Sync selectedUnits, dateRange, and liveMode with URL query params (no redux)
  const accountId = useSelector(getSelectedAccount).AccountId;

  const [selectedUnits, setSelectedUnitIdsInUrl] = useSelectedUnits();
  const [dateRange, setDateRangeInUrl] = useDateRange();
  const [liveMode, setLiveModeInUrl] = useLiveMode();

  const {
    units,
    chartSeries,
    isLoading,
    yAxes,
    seriesVisibility,
    setSeriesVisibility,
  } = useDataViewerData({
    accountId,
    selectedUnits,
    dateRange,
    liveMode,
    aggregateOverride:
      aggregateOverride ?? getAutoAggregateType(dateRange.from, dateRange.to),
  });

  // Toggle visibility for all series of a given data type
  const handleVisibleDataTypeToggle = (dataType) => {
    const idsForDt = chartSeries
      .filter((s) => s.dataType === dataType)
      .map((s) => s.id);
    setSeriesVisibility((prev) => {
      const anyOn = idsForDt.some((id) => prev[id]?.visible !== false);
      const next = { ...prev };
      idsForDt.forEach((id) => {
        next[id] = { visible: !anyOn };
      });
      return next;
    });
  };

  useEffect(() => {
    if (!liveMode) return;
    // Set date range to last hour and repeat every 5 seconds
    const updateDateRangeToLastHour = () => {
      const to = DateTime.now();
      const from = to.minus({ hours: 1 });
      setDateRangeInUrl({ from, to });
    };
    updateDateRangeToLastHour();
    const interval = setInterval(() => {
      updateDateRangeToLastHour();
    }, LIVEMODE_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [liveMode, setDateRangeInUrl]);

  return (
    <Container maxWidth="xxl">
      <Card variant="outlined">
        <CardHeader title={t('title')} />
        <CardContent>
          <Box display={'flex'} flexDirection="row" gap={2} mb={2}>
            <Box
              width={'100%'}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <UnitAutocomplete
                units={units}
                value={selectedUnits || []}
                onChange={(newValue) =>
                  setSelectedUnitIdsInUrl(newValue.map((u) => u.UnitId))
                }
              />
            </Box>
            {!liveMode && (
              <>
                <DateRangeSelector
                  from={
                    dateRange.from ? DateTime.fromISO(dateRange.from) : null
                  }
                  to={dateRange.to ? DateTime.fromISO(dateRange.to) : null}
                  onFromChange={(dt) =>
                    setDateRangeInUrl({ from: dt, to: dateRange.to })
                  }
                  onToChange={(dt) =>
                    setDateRangeInUrl({ from: dateRange.from, to: dt })
                  }
                  onToday={() => {
                    const now = DateTime.now();
                    const from = now.startOf('day');
                    const to = now;
                    setDateRangeInUrl({ from, to });
                  }}
                  DateTimePicker={DateTimePicker}
                />
                <AggregateTypeSelector
                  aggregateOverride={aggregateOverride}
                  options={AGGREGATE_OPTIONS}
                  onChange={setAggregateOverride}
                  getAggregateType={getAggregateType}
                  dateRange={{ from: dateRange.from, to: dateRange.to }}
                />
                <Divider orientation="vertical" flexItem />
              </>
            )}
            <Button
              variant={liveMode ? 'contained' : 'outlined'}
              onClick={() => setLiveModeInUrl(!liveMode)}
              color={liveMode ? 'primary' : 'inherit'}
            >
              {t('live')}
            </Button>
          </Box>
          <Box>
            <DataViewerChart
              isLoading={isLoading}
              seriesVisibility={seriesVisibility}
              setSeriesVisibility={setSeriesVisibility}
              chartSeries={chartSeries}
              dateRange={dateRange}
              yAxes={yAxes}
            />
          </Box>
          {/* DataType toggle buttons */}
          <Box justifyContent={'center'} mt={2} display="flex">
            <DataTypeToggleGroup
              dataTypes={Array.from(
                new Set(chartSeries.map((s) => s.dataType)),
              )}
              seriesVisibility={seriesVisibility}
              onToggle={handleVisibleDataTypeToggle}
              getIdsForDataType={(dt) =>
                chartSeries.filter((s) => s.dataType === dt).map((s) => s.id)
              }
            />
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DataViewerPage;
